import _ from 'lodash';
import moment from 'moment';
import pluralize from 'pluralize';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import styled from 'styled-components';
import {
  ClientLink,
  DateTime,
  ExportDropdown,
  Hours,
  InternalClientTooltip,
  PeriodFilter,
  ProjectLink,
  RouteLink,
  SingleSelectFilter,
  Widget,
} from '~/components';
import {
  ClientFiltersBar,
  ClientFiltersGroup,
  FilterButton,
  MemberFiltersBar,
  MemberFiltersGroup,
  ProjectFiltersBar,
  ProjectFiltersGroup,
  ProjectRoleFiltersBar,
  ProjectRoleFiltersGroup,
  ReportPeriodFilter,
  TaskFiltersBar,
  TaskFiltersGroup,
  TimeFiltersBar,
  TimeFiltersGroup,
} from '~/components/filters';
import { useApi, useConfirmation, useWorkspace } from '~/contexts';
import {
  useClientFilters,
  useDateTimeFormat,
  useDocumentTitle,
  useHoursFormat,
  useMemberFilters,
  useProjectFilters,
  useProjectRoleFilters,
  useSearchParams,
  useSearchParamsConfig,
  useTaskFilters,
  useTimeEntriesReportURL,
  useTimeFilters,
} from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import { dateFormats, mimeTypes } from '~/utils';
import ExportDialogAsync from '../components/ExportDialogAsync';
import Report from '../components/Report';
import Table from '../components/table';
import ReportActions from '../ReportActions';
import { intervals, intervalsByScope } from './intervals';
import ListTooltip from '../components/ListTooltip';
import List from '../components/List';

const Task = styled.div`
  display: flex;
  align-items: center;
  margin-left: 1rem;
`;

const Role = styled.div`
  display: flex;
  align-items: center;
  margin-left: 2rem;
`;

const Member = styled.div`
  display: flex;
  align-items: center;
  margin-left: 3rem;
`;

const ProjectInfo = styled.div`
  line-height: 1;

  small {
    font-size: 0.75rem;
    color: ${colors.grey40};
    display: flex;
    padding-top: 0.25rem;
  }
`;

function TimeSummaryByTaskAndRoleAndMemberByTimeUnit({ report }) {
  useDocumentTitle(report.name);
  const { workspace } = useWorkspace();
  const api = useApi();

  const [query, setQuery] = useState({ report: null, status: 'loading' });

  const clientFilters = useClientFilters();
  const projectFilters = useProjectFilters();
  const roleFilters = useProjectRoleFilters();
  const taskFilters = useTaskFilters();
  const memberFilters = useMemberFilters();
  const timeFilters = useTimeFilters();

  const [params, setParams] = useState({
    period: null,
    unit: '',

    ...memberFilters.filters,
    ...clientFilters.filters,
    ...projectFilters.filters,
    ...roleFilters.filters,
    ...taskFilters.filters,
    ...timeFilters.filters,
  });

  const searchParamsConfig = useSearchParamsConfig();
  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        period: {
          ...searchParamsConfig.periodByUnit,
          default: intervals.past_6_months,
          intervals: (unit) => intervalsByScope[unit],
          defaults: {
            day: intervals.past_30_days,
            week: intervals.past_12_weeks,
            month: intervals.past_6_months,
          },
        },
        unit: { default: 'month', valid: ['day', 'week', 'month'] },

        ...memberFilters.searchParamsConfig,
        ...clientFilters.searchParamsConfig,
        ...projectFilters.searchParamsConfig,
        ...roleFilters.searchParamsConfig,
        ...taskFilters.searchParamsConfig,
        ...timeFilters.searchParamsConfig,
      }),
      [searchParamsConfig, clientFilters, projectFilters, memberFilters, taskFilters, roleFilters, timeFilters],
    ),
    onChange: (params) => setParams((state) => ({ ...state, ...params })),
  });

  // Map the values to perform the API query
  const urlSearchParams = useMemo(
    () => ({
      start: params.period?.start ?? undefined,
      end: params.period?.end ?? undefined,
      unit: params.unit,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,

      ...memberFilters.mapUrlSearchParams(params),
      ...clientFilters.mapUrlSearchParams(params),
      ...projectFilters.mapUrlSearchParams(params),
      ...taskFilters.mapUrlSearchParams(params),
      ...roleFilters.mapUrlSearchParams(params),
      ...timeFilters.mapUrlSearchParams(params),
    }),
    [params, clientFilters, projectFilters, memberFilters, taskFilters, roleFilters, timeFilters],
  );

  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((params) => {
      setParams((state) => ({ ...state, ...params }));
      setSearchParamsStatus('ready');
    });
  }, [searchParams, searchParamsStatus]);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).reports(report.id).data(urlSearchParams);

    setQuery((state) => ({ ...state, data, status: 'ready' }));
  }, [api, workspace.id, report.id, urlSearchParams]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);
  const handleApplyFilters = (values) => {
    if (values !== params) setQuery((state) => ({ ...state, status: 'filtering' }));

    setParams({ ...params, ...values });
    searchParams.set(values);
    hideFilters();
  };

  const confirmation = useConfirmation();

  const handleExport = (mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialogAsync
        onLoad={api.www
          .workspaces(workspace.id)
          .reports(report.id)
          .data(urlSearchParams, { headers: { accept: mimeType } })}
        onClose={resolve}
      />
    ));
  };

  return (
    <Report>
      <Report.Header>
        <Report.Info report={report} />

        <Report.Actions>
          <ReportActions onSave={() => searchParams.set(params)} />
          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <FilterButton isOutline onClick={showFilters} />
        </Report.Actions>
      </Report.Header>

      <Report.FiltersBar>
        <ReportPeriodFilter
          value={params.period}
          intervals={intervalsByScope[params.unit]}
          onChange={({ value }) => handleApplyFilters({ period: value })}
        />

        <UnitFilter
          value={params.unit}
          onChange={({ target: { value } }) =>
            handleApplyFilters({
              unit: value,
              period: {
                day: intervals.past_30_days,
                week: intervals.past_12_weeks,
                month: intervals.past_6_months,
              }[value],
            })
          }
        />
        <ClientFiltersBar filters={params} onChange={handleApplyFilters} />
        <ProjectFiltersBar filters={params} onChange={handleApplyFilters} />
        <ProjectRoleFiltersBar filters={params} onChange={handleApplyFilters} />
        <TaskFiltersBar filters={params} onChange={handleApplyFilters} />
        <MemberFiltersBar filters={params} onChange={handleApplyFilters} />
        <TimeFiltersBar filters={params} onChange={handleApplyFilters} />
      </Report.FiltersBar>

      {(() => {
        switch (query.status) {
          case 'loading':
          case 'filtering':
            return <PageLoader />;

          default:
            return (
              <>
                <Data query={query} params={params} />
                <Filters values={params} isOpen={filtersVisible} onApply={handleApplyFilters} onClose={hideFilters} />
              </>
            );
        }
      })()}
    </Report>
  );
}

function Data({ query, params }) {
  const report = query.data;

  const { projects, records, periods, start, end } = useMemo(() => {
    const records = report.records.reduce(
      (a, v) => {
        const billable = v.billableHours || 0;
        const nonBillable = v.nonBillableHours || 0;
        const internal = v.internalHours || 0;
        const total = v.totalHours || 0;

        a.total.billable += billable;
        a.total.nonBillable += nonBillable;
        a.total.internal += internal;
        a.total.total += total;

        if (!a.periods[v.start]) {
          a.periods[v.start] = {
            start: v.start,
            end: v.end,
            billable: 0,
            nonBillable: 0,
            internal: 0,
            total: 0,
          };
        }
        a.periods[v.start].billable += billable;
        a.periods[v.start].nonBillable += nonBillable;
        a.periods[v.start].internal += internal;
        a.periods[v.start].total += total;

        if (v.projectId) {
          if (!a.projects[v.projectId]) {
            a.projects[v.projectId] = {
              total: { billable: 0, nonBillable: 0, internal: 0, total: 0 },
              periods: {},
              tasks: {},
            };
          }
          const project = a.projects[v.projectId];

          project.total.billable += billable;
          project.total.nonBillable += nonBillable;
          project.total.internal += internal;
          project.total.total += total;

          if (!project.periods[v.start]) {
            project.periods[v.start] = {
              start: v.start,
              end: v.end,
              billable: 0,
              nonBillable: 0,
              internal: 0,
              total: 0,
            };
          }
          project.periods[v.start].billable += billable;
          project.periods[v.start].nonBillable += nonBillable;
          project.periods[v.start].internal += internal;
          project.periods[v.start].total += total;

          if (!project.tasks[v.projectTaskId]) {
            project.tasks[v.projectTaskId] = {
              total: { billable: 0, nonBillable: 0, internal: 0, total: 0 },
              periods: {},
              roles: {},
            };
          }
          const task = project.tasks[v.projectTaskId];

          task.total.billable += billable;
          task.total.nonBillable += nonBillable;
          task.total.internal += internal;
          task.total.total += total;

          if (!task.periods[v.start]) {
            task.periods[v.start] = {
              start: v.start,
              end: v.end,
              billable: 0,
              nonBillable: 0,
              internal: 0,
              total: 0,
            };
          }
          task.periods[v.start].billable += billable;
          task.periods[v.start].nonBillable += nonBillable;
          task.periods[v.start].internal += internal;
          task.periods[v.start].total += total;

          if (v.projectRoleId) {
            if (!task.roles[v.projectRoleId]) {
              task.roles[v.projectRoleId] = {
                total: { billable: 0, nonBillable: 0, internal: 0, total: 0 },
                periods: {},
                members: {},
              };
            }
            const role = task.roles[v.projectRoleId];

            role.total.billable += billable;
            role.total.nonBillable += nonBillable;
            role.total.internal += internal;
            role.total.total += total;

            if (!role.periods[v.start]) {
              role.periods[v.start] = {
                start: v.start,
                end: v.end,
                billable: 0,
                nonBillable: 0,
                internal: 0,
                total: 0,
              };
            }
            role.periods[v.start].billable += billable;
            role.periods[v.start].nonBillable += nonBillable;
            role.periods[v.start].internal += internal;
            role.periods[v.start].total += total;

            if (v.memberId) {
              if (!role.members[v.memberId]) {
                role.members[v.memberId] = {
                  total: { billable: 0, nonBillable: 0, internal: 0, total: 0 },
                  periods: {},
                };
              }
              const member = role.members[v.memberId];

              member.total.billable += billable;
              member.total.nonBillable += nonBillable;
              member.total.internal += internal;
              member.total.total += total;

              if (!member.periods[v.start]) {
                member.periods[v.start] = {
                  start: v.start,
                  end: v.end,
                  billable: 0,
                  nonBillable: 0,
                  internal: 0,
                  total: 0,
                };
              }
              member.periods[v.start].billable += billable;
              member.periods[v.start].nonBillable += nonBillable;
              member.periods[v.start].internal += internal;
              member.periods[v.start].total += total;
            }
          }
        }

        return a;
      },
      {
        periods: {},
        total: { billable: 0, nonBillable: 0, internal: 0, total: 0 },
        projects: {},
      },
    );

    let start = params.period.start;
    let end = params.period.end;

    switch (params.unit) {
      case 'day':
        start = moment(start).format(dateFormats.isoDate);
        end = moment(end).format(dateFormats.isoDate);
        break;

      case 'week':
        start = moment(start).startOf('isoWeek').format(dateFormats.isoDate);
        end = moment(end).endOf('isoWeek').format(dateFormats.isoDate);
        break;

      case 'month':
        start = moment(start).startOf('month').format(dateFormats.isoDate);
        end = moment(end).endOf('month').format(dateFormats.isoDate);
        break;
    }

    const periodCount = moment(end).diff(start, params.unit) + 1;
    const periods = [];
    for (let index = 0; index < periodCount; index++) {
      const periodStart = moment(start).add(index, params.unit).format(dateFormats.isoDate);
      const periodEnd = moment(periodStart)
        .endOf({ day: 'day', week: 'isoWeek', month: 'month' }[params.unit])
        .format(dateFormats.isoDate);
      periods.push({ start: periodStart, end: periodEnd });
    }

    return {
      projects: report.projects,
      records,
      periods,
      start,
      end,
    };
  }, [report, params.period.start, params.period.end, params.unit]);

  const timeDetail = useTimeEntriesReportURL({
    params,
    query: {
      start,
      end,
      timeBillableTypeId: ['billable', 'non_billable', 'internal'],
    },
  });

  return (
    <>
      <Report.Summary>
        <Chart periods={periods} records={records.periods} unit={params.unit} />
      </Report.Summary>

      <Report.Table>
        <Table>
          <Table.Header>
            <Table.Column sticky minWidth="16rem">
              Project/Client
            </Table.Column>

            {periods.map(({ start }) => (
              <Table.Column key={start} align="right" width="7rem">
                {
                  {
                    day: <DateTime value={start} />,
                    week: <DateTime value={start} />,
                    month: moment(start).format(dateFormats.monthYear),
                  }[params.unit]
                }
              </Table.Column>
            ))}

            <Table.Column align="right" width="7rem">
              Total
            </Table.Column>
          </Table.Header>

          <Table.Body>
            {projects.map((project) => (
              <React.Fragment key={project.id}>
                <Table.Row style={{ fontWeight: weights.bold }}>
                  <Table.Cell>
                    <ProjectInfo>
                      <p>
                        <ProjectLink project={project} />
                      </p>
                      <small>
                        <ClientLink client={project.client} />
                        {project.client.isInternal && <InternalClientTooltip />}
                      </small>
                    </ProjectInfo>
                  </Table.Cell>

                  {periods.map((period) => {
                    const value = records.projects[project.id]?.periods[period.start];

                    return (
                      <Table.Cell key={period.start}>
                        <CellValue
                          value={value}
                          to={timeDetail({
                            projectId: project.id,
                            projectTaskId: project.tasks.map((task) => task.id),
                            start: period.start,
                            end: period.end,
                          })}
                        />
                      </Table.Cell>
                    );
                  })}

                  <Table.Cell>
                    <CellValue
                      value={records.projects[project.id]?.total}
                      to={timeDetail({
                        projectId: project.id,
                        projectTaskId: project.tasks.map((task) => task.id),
                      })}
                    />
                  </Table.Cell>
                </Table.Row>

                {project.tasks.map((task) => (
                  <React.Fragment key={task.id}>
                    <Table.Row>
                      <Table.Cell>
                        <Task>{task.name}</Task>
                      </Table.Cell>

                      {periods.map((period) => {
                        const value = records.projects[project.id]?.tasks?.[task.id]?.periods[period.start];

                        return (
                          <Table.Cell key={period.start}>
                            <CellValue
                              value={value}
                              to={timeDetail({
                                projectId: task.projectId,
                                projectTaskId: task.id,
                                start: period.start,
                                end: period.end,
                              })}
                            />
                          </Table.Cell>
                        );
                      })}

                      <Table.Cell>
                        <CellValue
                          value={records.projects[project.id]?.tasks?.[task.id]?.total}
                          to={timeDetail({ projectId: task.projectId, projectTaskId: task.id })}
                        />
                      </Table.Cell>
                    </Table.Row>

                    {task.roles.map((role) => (
                      <React.Fragment key={role.id}>
                        <Table.Row>
                          <Table.Cell>
                            <Role>{role.name}</Role>
                          </Table.Cell>

                          {periods.map((period) => {
                            const value =
                              records.projects[project.id]?.tasks?.[task.id]?.roles[role.id]?.periods[period.start];
                            return (
                              <Table.Cell key={period.start}>
                                <CellValue
                                  value={value}
                                  to={timeDetail({
                                    projectId: task.projectId,
                                    projectTaskId: task.id,
                                    projectRoleId: role.id,
                                    start: period.start,
                                    end: period.end,
                                  })}
                                />
                              </Table.Cell>
                            );
                          })}

                          <Table.Cell>
                            <CellValue
                              value={records.projects[project.id]?.tasks?.[task.id]?.roles[role.id]?.total}
                              to={timeDetail({
                                projectId: task.projectId,
                                projectTaskId: task.id,
                                projectRoleId: role.id,
                              })}
                            />
                          </Table.Cell>
                        </Table.Row>

                        {role.members.map((projectMember) => (
                          <Table.Row key={projectMember.member?.id}>
                            <Table.Cell>
                              <Member>{projectMember.member?.name}</Member>
                            </Table.Cell>

                            {periods.map((period) => {
                              const value =
                                records.projects[project.id]?.tasks?.[task.id]?.roles[role.id]?.members[
                                  projectMember.member?.id
                                ]?.periods[period.start];
                              return (
                                <Table.Cell key={period.start}>
                                  <CellValue
                                    value={value}
                                    to={timeDetail({
                                      projectId: task.projectId,
                                      projectTaskId: task.id,
                                      projectRoleId: role.id,
                                      memberId: projectMember.member?.id,
                                      start: period.start,
                                      end: period.end,
                                    })}
                                  />
                                </Table.Cell>
                              );
                            })}

                            <Table.Cell>
                              <CellValue
                                value={
                                  records.projects[project.id]?.tasks?.[task.id]?.roles[role.id]?.members[
                                    projectMember.member?.id
                                  ]?.total
                                }
                                to={timeDetail({
                                  projectId: task.projectId,
                                  projectTaskId: task.id,
                                  projectRoleId: role.id,
                                  memberId: projectMember.member?.id,
                                })}
                              />
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}

            <Table.Row style={{ fontWeight: weights.bold }}>
              <Table.Cell>Total</Table.Cell>

              {periods.map((period) => {
                const value = records.periods[period.start];

                return (
                  <Table.Cell key={period.start}>
                    <CellValue value={value} />
                  </Table.Cell>
                );
              })}

              <Table.Cell>
                <CellValue value={records.total} />
              </Table.Cell>
            </Table.Row>
          </Table.Body>

          <Table.Status
            total={projects.flatMap((project) => project.tasks).length}
            label="Task"
            isLoading={query.status !== 'ready'}
          />
        </Table>
      </Report.Table>
    </>
  );
}

function Filters({ values, isOpen, onClose, onApply }) {
  const [filters, setFilters] = useState(values);

  const handleApply = () => {
    onApply(filters);
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <Report.FiltersDrawer isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      <UnitFilter
        value={filters.unit}
        onChange={({ target: { value } }) =>
          handleFilter({
            unit: value,
            period: {
              day: intervals.past_30_days,
              week: intervals.past_12_weeks,
              month: intervals.past_6_months,
            }[value],
          })
        }
      />

      <PeriodFilter
        clearable={false}
        scope={filters.unit}
        intervals={intervalsByScope[filters.unit]}
        placeholder="Date Range"
        value={filters.period}
        onChange={({ target: { value } }) => handleFilter({ period: value })}
      />

      <ClientFiltersGroup filters={filters} onChange={handleFilter} />

      <ProjectFiltersGroup filters={filters} onChange={handleFilter} />

      <ProjectRoleFiltersGroup filters={filters} onChange={handleFilter} />

      <TaskFiltersGroup filters={filters} onChange={handleFilter} />

      <MemberFiltersGroup filters={filters} onChange={handleFilter} />

      <TimeFiltersGroup filters={filters} onChange={handleFilter} />
    </Report.FiltersDrawer>
  );
}

function CellValue({ value, to }) {
  if (!value)
    return to ? (
      <RouteLink to={to}>
        <Hours value={null} />
      </RouteLink>
    ) : (
      <Hours value={null} />
    );

  return (
    <TimeSummaryTooltip value={value}>
      {to ? (
        <RouteLink to={to}>
          <Hours value={value.total} />
        </RouteLink>
      ) : (
        <Hours value={value.total} />
      )}
    </TimeSummaryTooltip>
  );
}

const getMax = (periods, defaultMax = 8, precision = 2) => {
  let max = Math.max(...periods.map(({ total }) => Math.round(total) || 0), defaultMax) * 1.1;
  max = Math.floor(max / precision) * precision + precision;

  return max;
};

function TimeSummaryTooltip({ value, children }) {
  if (!value) return null;

  return (
    <ListTooltip
      message={
        <List>
          <List.Item label="Client Billable">
            <span>
              <Hours value={value.billable} /> {_.isNumber(value.billable) && pluralize('hours', value.billable)}
            </span>
          </List.Item>
          <List.Item label="Client Non-Billable">
            <span>
              <Hours value={value.nonBillable} />{' '}
              {_.isNumber(value.nonBillable) && pluralize('hours', value.nonBillable)}
            </span>
          </List.Item>
          <List.Item label="Internal">
            <span>
              <Hours value={value.internal} /> {_.isNumber(value.internal) && pluralize('hours', value.internal)}
            </span>
          </List.Item>
          <List.Item label="Total">
            <span>
              <Hours value={value.total} /> {_.isNumber(value.total) && pluralize('hours', value.total)}
            </span>
          </List.Item>
        </List>
      }>
      {children}
    </ListTooltip>
  );
}

function Chart({ periods, records, unit }) {
  const dateTimeFormat = useDateTimeFormat(dateFormats.compactDate);

  const { settings, datasets } = useMemo(() => {
    const data = periods.map(({ start }) => ({
      start,
      billable: records[start]?.billable || 0,
      nonBillable: records[start]?.nonBillable || 0,
      internal: records[start]?.internal || 0,
      total: records[start]?.total || 0,
    }));

    const settings = {
      day: () => ({
        labels: data.map(({ start }) => dateTimeFormat.format(start)),
        max: getMax(data, 8, 2),
        stepSize: 2,
      }),
      week: () => ({
        labels: data.map(({ start }) => dateTimeFormat.format(start)),
        max: getMax(data, 8, 2),
        stepSize: 10,
      }),
      month: () => ({
        labels: data.map(({ start }) => moment(start).format(dateFormats.monthYear)),
        max: getMax(data, 8, 2),
        stepSize: 20,
      }),
    }[unit]();

    const datasets = [
      {
        id: 'billable',
        label: 'Client Billable    ',
        data: data.map(({ billable }) => billable),
        backgroundColor: colors.success,
      },
      {
        id: 'non-billable',
        label: 'Client Non-Billable    ',
        data: data.map(({ nonBillable }) => nonBillable),
        backgroundColor: colors.danger,
      },
      {
        id: 'internal',
        label: 'Internal    ',
        data: data.map(({ internal }) => internal),
        backgroundColor: colors.warning,
      },
      {
        id: 'total',
        label: 'Total',
        data: data.map(({ total }) => (total ? 0 : 0.25)),
        backgroundColor: colors.grey5,
      },
    ];

    return { settings, datasets };
  }, [periods, records, unit, dateTimeFormat]);

  const hoursFormat = {
    tooltip: useHoursFormat({ minimumFractionDigits: 0, maximumFractionDigits: 2 }),
  };

  return (
    <Widget style={{ height: '20rem' }}>
      <Bar
        data={{
          labels: settings.labels,
          datasets: datasets,
        }}
        options={{
          maintainAspectRatio: false,

          layout: {
            padding: { top: 12 },
          },

          plugins: {
            legend: {
              onClick: null,
              labels: {
                font: { size: 12 },
                pointStyleWidth: 14,
                boxHeight: 10,
                filter: (item) => !item.text.includes('Total'),
                usePointStyle: true,
              },
            },

            tooltip: {
              filter: (item) => item.datasetIndex <= 2,
              callbacks: {
                title: () => '',
                label: (tooltip) => {
                  let label = (tooltip.dataset.label || '').trim();
                  if (label) {
                    label += ': ';
                  }
                  label += hoursFormat.tooltip.format(tooltip.parsed.y);
                  return label;
                },
              },
            },
          },

          scales: {
            x: {
              stacked: true,
              grid: { display: false },
              ticks: {
                font: {
                  size: 12,
                  weight: 'bold',
                },
                color: colors.grey40,
                minRotation: settings.rotation,
                maxRotation: settings.rotation,
              },
            },

            y: {
              display: true,
              stacked: true,
              grid: { display: true, color: colors.grey10 },
              border: { display: false },
              max: settings.max,
              ticks: { stepSize: settings.stepSize, color: colors.grey40 },
            },
          },
        }}
      />
    </Widget>
  );
}

function UnitFilter(props) {
  return (
    <SingleSelectFilter
      icon="gear"
      placeholder="Unit"
      clearable={false}
      renderValue={(value) => value.name}
      options={[
        { id: 'month', name: 'Month' },
        { id: 'week', name: 'Week' },
        { id: 'day', name: 'Day' },
      ]}
      {...props}
    />
  );
}

export default TimeSummaryByTaskAndRoleAndMemberByTimeUnit;
